/**
 * MessageList 的纯逻辑口径（任务 9e-1，可单测）：组件（MessageList.tsx）保持
 * 纯展示，尺寸/计数的规则都锁在这里。
 */
import { MsgType, msgTypeSpec } from '~/chat/msgTypes';
import { mentionsMe, parseTextEnvelope } from '~/chat/preview';
import type { StoredMessage } from '~/worker/contract';

/** 图片气泡显示尺寸上限（CSS 像素）；与 messageList.module.scss 的气泡宽度配合 */
export const IMAGE_MAX_W = 240;
export const IMAGE_MAX_H = 320;
/** 宽高未知（envelope 缺字段/0）时的方形占位边长——加载后由真实尺寸接管会跳一次，
 * 占位优先保证虚拟列表测高稳定 */
export const IMAGE_FALLBACK_SIZE = 160;

/** 等比缩进 maxW×maxH 盒（不放大）；未知尺寸回方形占位 */
export function imageBoxSize(
  width: number,
  height: number,
): { width: number; height: number } {
  if (width <= 0 || height <= 0) {
    return { width: IMAGE_FALLBACK_SIZE, height: IMAGE_FALLBACK_SIZE };
  }
  const scale = Math.min(IMAGE_MAX_W / width, IMAGE_MAX_H / height, 1);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** 贴纸形态尺寸上限（14.16b；kind=sticker 的图片兼容通道还原为定宽贴纸） */
export const STICKER_BOX = 120;

/** 贴纸等比缩进 STICKER_BOX 方盒（不放大）；未知尺寸方形占位（测高稳定同策） */
export function stickerBoxSize(
  width: number,
  height: number,
): { width: number; height: number } {
  if (width <= 0 || height <= 0) return { width: STICKER_BOX, height: STICKER_BOX };
  const scale = Math.min(STICKER_BOX / width, STICKER_BOX / height, 1);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** 文件/视频卡片（14.3）：固定宽度——虚拟列表测高稳定（语音气泡同策） */
export const FILE_CARD_WIDTH = 230;

/**
 * 文件大小展示（14.3）：B/KB/MB/GB，1 位小数去尾零；≤0（envelope 缺字段）
 * 返回空串由调用方隐藏该段。
 */
export function formatFileSize(bytes: number): string {
  if (bytes <= 0) return '';
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  const units = ['KB', 'MB', 'GB'] as const;
  let v = bytes;
  let unit = '';
  for (const u of units) {
    v /= 1024;
    unit = u;
    if (v < 1024) break;
  }
  const s = v >= 100 ? String(Math.round(v)) : v.toFixed(1).replace(/\.0$/, '');
  return `${s} ${unit}`;
}

/** 语音气泡（14.2a）：条数/宽度与旧版 _VoiceMessageContent 同口径 */
export const VOICE_BAR_COUNT = 24;
export const VOICE_BAR_MAX_H = 24;

/** 装饰性波形高度表（协议无真实波形数据，旧版 _fallbackWave 同值，按下标循环） */
const VOICE_WAVE: readonly number[] = [
  0.35, 0.55, 0.42, 0.76, 0.5, 0.88, 0.64, 0.38, 0.7, 0.56, 0.84, 0.48, 0.68, 0.44, 0.6,
];

/** 第 index 根波形条的高度（px，确定性——虚拟列表测高稳定） */
export function voiceBarHeight(index: number): number {
  return Math.round(VOICE_WAVE[index % VOICE_WAVE.length]! * VOICE_BAR_MAX_H);
}

/**
 * 非 follow 态（用户在看历史）新 append 的「对方内容消息」数——新消息胶囊计数。
 * rows 升序，从尾部扫到 id ≤ afterId 为止；system-note/signal 与自己的消息不计。
 */
export function countNewIncoming(
  rows: readonly StoredMessage[],
  afterId: number,
  myUid: number,
): number {
  let n = 0;
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    const row = rows[i]!;
    if (row.id <= afterId) break;
    if (row.userId !== myUid && msgTypeSpec(row.msgType).category === 'content') n += 1;
  }
  return n;
}

/** 行是否「对方发的文本且 @ 到我」（14.11b；提及只存在于文本 envelope 的 t/g） */
function rowMentionsMe(row: StoredMessage, myUid: number): boolean {
  return (
    row.userId !== myUid &&
    row.msgType === MsgType.Text &&
    mentionsMe(parseTextEnvelope(row.msg), myUid)
  );
}

/**
 * 新 append 的行里是否有 @ 我的（14.11b 被@钮置起；rows 升序，从尾扫到
 * id ≤ afterId 为止，与 countNewIncoming 同窗口口径）。
 */
export function hasNewMention(
  rows: readonly StoredMessage[],
  afterId: number,
  myUid: number,
): boolean {
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    const row = rows[i]!;
    if (row.id <= afterId) break;
    if (rowMentionsMe(row, myUid)) return true;
  }
  return false;
}

/**
 * 已加载窗口内最新一条 @ 我的消息 id（14.11b 被@钮点击目标；新→旧找，旧版
 * 同口径）。找不到返回 0（调用方滚到底兜底）。
 */
export function latestMentionId(rows: readonly StoredMessage[], myUid: number): number {
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    const row = rows[i]!;
    if (rowMentionsMe(row, myUid)) return row.id;
  }
  return 0;
}
